import { Button, ButtonGroup, Chip, Tooltip } from '@nextui-org/react';
import { useMemo, useState } from 'react';
import { FaThumbsDown, FaThumbsUp } from 'react-icons/fa6';
import Icon from '../../base/Icon';
import { motion, useAnimate } from 'framer-motion';
import { AppHue, MessageRole, PromptType, SuicideLabel } from '../../../types';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  riskToLabel,
  selectRiskOfMessages,
} from '../../../store/assistant/assistantSlice';
import { selectAssistantMessageScore } from '../../../store/chat/chatSlice';
import { setAssistantMessageScoreThunk } from '../../../store/chat/chatThunks';
import { AssistantMessageScoreDto } from '../../../store/chat/chatDto';
import ChatBubbleSingle, { ChatBubbleSingleProps } from './ChatBubbleSingle';
import ChatBubbleMultiple from './ChatBubbleMultiple';
import {
  formatIsoToLocaleDateTime,
  getSuicidalityLabel,
  MOTION_KEYS,
  quoteMessage,
} from '../../../util';
import {
  selectSettingsAnalytics,
  selectSettingsDeveloper,
} from '../../../store/settings/settingsSlice';
import { createPortal } from 'react-dom';

const SCORE_UP = 1;
const SCORE_DOWN = -1;

export interface ChatBubbleProps {
  messageId: string;
  message: string;
  role: MessageRole;
  hue?: AppHue;
  modelTag?: string;
  timestamp?: string;
  sentiment?: number;
  promptType?: PromptType;
  showVoteButtons?: boolean;
  showRiskIndicator?: boolean;
  suicidalityLabel?: SuicideLabel;
  suicidalityScore?: number;
  onTransfer?: (message: string, sendImmediately?: boolean) => void;
}

export default function ChatBubble({
  messageId,
  message,
  role,
  hue,
  modelTag,
  timestamp,
  sentiment,
  promptType = 'direct',
  showVoteButtons,
  showRiskIndicator,
  suicidalityLabel,
  suicidalityScore,
  onTransfer,
}: ChatBubbleProps) {
  const dispatch = useAppDispatch();
  const settingsDev = useAppSelector(selectSettingsDeveloper);
  const riskOfMessages = useAppSelector(selectRiskOfMessages);
  const score = useAppSelector(selectAssistantMessageScore(messageId));
  const [scopeThumbsUp, animateThumbsUp] = useAnimate<HTMLDivElement>();
  const [scopeThumbsDown, animateThumbsDown] = useAnimate<HTMLDivElement>();
  const settingsAnalytics = useAppSelector(selectSettingsAnalytics);

  const handleScoreChange = (newScore: number) => () => {
    const dto: AssistantMessageScoreDto = {
      message_assistant_id: messageId,
      score: newScore,
    };
    if (score === newScore) {
      dto.score = 0;
    } else {
      switch (newScore) {
        case SCORE_UP:
          animateThumbsUp(scopeThumbsUp.current, {
            y: [0, 2, -10, 2, 0],
            rotate: [0, 0, 180, 360, 360],
          });
          break;
        case SCORE_DOWN:
          animateThumbsDown(scopeThumbsDown.current, {
            y: [0, -2, 10, -2, 0],
            rotate: [0, 0, 30, -15, 0],
          });
          break;
      }
    }
    dispatch(setAssistantMessageScoreThunk(dto));
  };

  const quotedMessage = useMemo(
    () => quoteMessage(message, promptType),
    [message, promptType]
  );

  const riskLabel = useMemo(() => {
    if (riskOfMessages[message]) {
      if (riskOfMessages[message].risk)
        // @ts-ignore
        return riskToLabel(riskOfMessages[message].risk);
      else return { content: 'Unknown risk', color: 'default' };
    }
  }, [message, riskOfMessages]);

  const reasoningTrace = useMemo(() => {
    if (riskOfMessages[message]) {
      return riskOfMessages[message].reasoning_trace;
    }
    return null;
  }, [message, riskOfMessages]);

  const [showPopup, setShowPopup] = useState(false);

  const riskContent = useMemo(
    () =>
      reasoningTrace ? (
        <button
          type="button"
          className="risk-label-button"
          onClick={() => setShowPopup(true)}
        >
          {riskLabel?.content} (click for reasoning)
        </button>
      ) : (
        riskLabel?.content
      ),
    [reasoningTrace, riskLabel]
  );

  const riskIndicator = useMemo(
    () =>
      showRiskIndicator && riskLabel && !showPopup ? (
        // @ts-ignore
        <Tooltip showArrow content={riskContent} color={riskLabel.color}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.2 }}
            className={
              'w-4 h-4 rounded-full shadow-small border-2 ' +
              `${
                (riskLabel.color === 'default' &&
                  'bg-neutral-100 border-neutral-300') ||
                (riskLabel.color === 'danger' &&
                  'bg-danger-500 border-danger-100') ||
                (riskLabel.color === 'warning' &&
                  'bg-warning-500 border-warning-100') ||
                (riskLabel.color === 'secondary' &&
                  'bg-secondary-500 border-secondary-100')
              }`
            }
          />
        </Tooltip>
      ) : undefined,
    [showRiskIndicator, riskLabel, riskContent, showPopup]
  );

  const popup = useMemo(
    () =>
      showPopup
        ? createPortal(
            <div className="popup-overlay" onClick={() => setShowPopup(false)}>
              <div className="popup" onClick={(e) => e.stopPropagation()}>
                <p className="reasoning-text">{reasoningTrace}</p>
                <button
                  type="button"
                  className="close-button"
                  onClick={() => setShowPopup(false)}
                >
                  Close
                </button>
              </div>
            </div>,
            document.body
          )
        : null,
    [showPopup, reasoningTrace]
  );

  const timestampChip = useMemo(() => {
    if (!timestamp) {
      return;
    }
    const formatted = formatIsoToLocaleDateTime(timestamp);
    if (!formatted) {
      return;
    }
    return (
      <Chip size="sm" variant="flat" className="text-gray-700 italic">
        {formatted}
      </Chip>
    );
  }, [timestamp]);

  const sentimentChip = useMemo(() => {
    if (
      typeof sentiment !== 'number' ||
      !settingsAnalytics.autoEvaluateSentiment
    ) {
      return;
    }
    const opacity = Math.min(Math.max(sentiment, -1), 1);
    const positiveOpacity = Math.max(0, opacity);
    const negativeOpacity = -Math.min(0, opacity);
    return (
      <Tooltip showArrow content="Sentiment (individual message only)">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.2 }}
          className="relative px-2 py-1 rounded-full shadow-small overflow-hidden bg-neutral-100"
        >
          <div
            className="absolute w-full h-full left-0 top-0 bg-success-200"
            style={{ opacity: positiveOpacity }}
          />
          <div
            className="absolute w-full h-full left-0 top-0 bg-danger-200"
            style={{ opacity: negativeOpacity }}
          />
          <p className="relative text-xs text-center text-gray-700">
            {sentiment.toFixed(2)}
          </p>
        </motion.div>
      </Tooltip>
    );
  }, [sentiment, settingsAnalytics]);

  const suicidalityChip = useMemo(() => {
    if (
      typeof suicidalityScore !== 'number' ||
      !settingsAnalytics.evaluateSuicidality
    ) {
      return;
    }
    return (
      <Tooltip
        showArrow
        content={
          <span>
            Label Confidence: {(suicidalityScore * 100).toFixed(1)}% (individual
            message only)
          </span>
        }
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.2 }}
          className="relative px-2 py-1 rounded-full shadow-small overflow-hidden bg-neutral-100"
        >
          <p className="relative text-xs text-center text-gray-700">
            {getSuicidalityLabel(suicidalityLabel)}
          </p>
        </motion.div>
      </Tooltip>
    );
  }, [suicidalityScore, suicidalityLabel, settingsAnalytics]);

  const modelTagChip = useMemo(
    () =>
      modelTag && settingsDev.showMessageModelTag ? (
        <Chip
          size="sm"
          variant="flat"
          className="text-gray-700 italic bg-gradient-to-br from-blue-100 to-red-100"
        >
          {modelTag}
        </Chip>
      ) : undefined,
    [modelTag, settingsDev]
  );

  const upDownVoteButtons = showVoteButtons ? (
    <ButtonGroup isIconOnly size="sm" radius="full" variant="flat">
      <Button
        color={score === SCORE_UP ? 'primary' : 'default'}
        onPress={handleScoreChange(SCORE_UP)}
      >
        <div ref={scopeThumbsUp}>
          <Icon icon={FaThumbsUp} />
        </div>
      </Button>
      <Button
        color={score === SCORE_DOWN ? 'primary' : 'default'}
        onPress={handleScoreChange(SCORE_DOWN)}
      >
        <div ref={scopeThumbsDown}>
          <Icon icon={FaThumbsDown} />
        </div>
      </Button>
    </ButtonGroup>
  ) : undefined;

  const messageBubble = useMemo(() => {
    const props: ChatBubbleSingleProps = {
      messageId,
      message,
      role,
      onTransfer,
      showTransfer: true,
      showEvaluateRisk:
        role === 'assistant' &&
        hue === 'client' &&
        settingsAnalytics.autoEvaluateMessageRisk,
      showEvaluateSentiment:
        role === 'assistant' &&
        hue === 'client' &&
        settingsAnalytics.autoEvaluateSentiment,
      showEvaluateSuicidality:
        role === 'assistant' &&
        hue === 'client' &&
        settingsAnalytics.evaluateSuicidality,
    };
    switch (role) {
      case 'assistant':
        switch (hue) {
          case 'assistant':
            return <ChatBubbleMultiple {...props} />;
          case 'client':
            if (message.includes('<break>')) {
              return <ChatBubbleMultiple {...props} />;
            }
            return <ChatBubbleSingle {...props} />;
        }
        break;
      case 'user':
        props.hue = hue;
        if (hue === 'assistant') {
          props.message = quotedMessage;
        }
        return <ChatBubbleSingle {...props} />;
    }
  }, [
    messageId,
    message,
    quotedMessage,
    role,
    hue,
    settingsAnalytics,
    onTransfer,
  ]);

  return (
    <motion.div
      variants={{
        [MOTION_KEYS.ANIMATE]: { opacity: [0, 1] },
        [MOTION_KEYS.EXIT]: { opacity: 0 },
      }}
      className={`relative max-w-[calc(100%-2rem)] flex flex-col gap-2 ${
        role === 'assistant'
          ? 'items-start self-start mr-4'
          : 'items-end self-end ml-4'
      }`}
    >
      <motion.div key={messageId} layoutId={messageId} layout="position">
        {messageBubble}
      </motion.div>
      <div
        className={`flex items-center gap-2 ${
          role === 'assistant' ? 'flex-row' : 'flex-row-reverse'
        }`}
      >
        {timestampChip}
        {sentimentChip}
        {suicidalityChip}
        {modelTagChip}
        {upDownVoteButtons}
        {riskIndicator}
        {popup}
      </div>
    </motion.div>
  );
}
